/*
 * scheduleUtils
 *
 * Small, focused utility helpers for the weekly schedule UI. These functions
 * implement the transformations required by the components in this folder.
 *
 * Public functions:
 * - formatTime12(timeStr: 'HH:MM') -> 'h[:mm] AM|PM'
 * - getDurationInHours(start: 'HH:MM', end: 'HH:MM') -> float hours
 * - getColorByType(typeArr: string[]) -> hex color string
 * - getGridHours(data: BackendPayload) -> string[] of hour labels for the time column
 * - buildScheduleGrid(data: BackendPayload) -> { DAY: Array<EntryWithPositioning> }
 * - formatTimeRangeCompact(start, end) -> compact human-readable range
 *
 * Backend payload shape:
 * - The backend returns an array where each item is a one-key object:
 *   { "Class Name": { start_time: 'HH:MM', end_time: 'HH:MM', days: ['MON'], level: [...], class_type: [...] } }
 * - buildScheduleGrid returns per-day arrays with added positioning metadata
 *   (topOffset in minutes from 12:00 PM) and overlap hints.
 *
 * Edge cases and behavior:
 * - getGridHours defaults the start of the rendered grid to 12:00 PM if the
 *   payload contains earlier hours
 */

/**
 * formatTime12(timeStr)
 * Convert a 24-hour 'HH:MM' string into a 12-hour formatted string with
 * AM/PM suffix. Minutes are omitted when they are ':00'.
 *
 * Examples:
 * - '18:00' -> '6 PM'
 * - '09:30' -> '9:30 AM'
 *
 * Input: timeStr: 'HH:MM' (string)
 * Output: formatted string like '6 PM' or '9:30 AM'
 */
export function formatTime12(timeStr) {
  const [h, m] = timeStr.split(':');
  const hour = parseInt(h);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const formatted = `${((hour % 12) || 12)}${m !== '00' ? `:${m}` : ''} ${suffix}`;
  return formatted;
}

/**
 * getDurationInHours(start, end)
 * Compute the duration between two 'HH:MM' times and return the result in
 * hours as a floating point number. Used to convert class durations into
 * pixel heights in the schedule grid.
 *
 * Assumes end occurs after start on the same day.
 */
export function getDurationInHours(start, end) {
  const [hs, ms] = start.split(':').map(Number);
  const [he, me] = end.split(':').map(Number);
  return ((he * 60 + me) - (hs * 60 + ms)) / 60;
}

/**
 * getColorByType(typeArr)
 * Small helper to pick a background color for a class block based on its
 * type codes. The UI stores type codes (short strings) and we map common
 * combinations to consistent colors.
 *
 * Input: typeArr: array of strings e.g. ['ST'] or ['GR'] or ['ST','GR']
 * Output: hex color string
 */
export function getColorByType(typeArr) {
  if (typeArr.includes('ST') && typeArr.includes('GR')) return '#555'; // hybrid
  if (typeArr.includes('ST')) return '#1976d2'; // striking = blue
  if (typeArr.includes('GR')) return '#9c27b0'; // grappling = purple
  return '#444';
}


export function getGridHours(data) {
  // Build an array of hour labels to render vertically in the time column.
  // We look at the raw schedule payload to find the earliest starting hour
  // and the latest ending time so the grid covers the full range.
  let minHour = Infinity;
  let maxMinutes = -Infinity;

  data.forEach(entry => {
    // Each 'entry' in the backend payload has one key (class name) whose
    // value contains start_time/end_time. We extract that first value.
    const values = Object.values(entry)[0];
    const startParts = values.start_time.split(':').map(Number);
    const endParts = values.end_time.split(':').map(Number);

    minHour = Math.min(minHour, startParts[0]);
    const endTotalMin = endParts[0] * 60 + endParts[1];
    maxMinutes = Math.max(maxMinutes, endTotalMin);
  });

  // Default schedule start is noon (12) if data contains earlier times
  // this will expand the grid accordingly.
  const startHour = (minHour === Infinity ? 12 : minHour);
  // lastLabelHour computes the last whole hour label to render
  const lastLabelHour = Math.ceil(maxMinutes / 60) - 1;

  const hours = [];
  for (let h = startHour; h <= lastLabelHour; h++) {
    hours.push(formatTime12(`${h}:00`));
  }

  return hours;
}

export function buildScheduleGrid(data, earliestHour = 12) {
  // Transform the backend schedule payload into a map keyed by day of week
  // where each value is an array of class entries augmented with
  // positioning metadata (topOffset) and placeholders for overlap info.
  const result = {};

  data.forEach(entry => {
    // backend shape: { "Class Name": { start_time: 'HH:MM', end_time: 'HH:MM', days: [...], level: [...] } }
    const [className, values] = Object.entries(entry)[0];
    const { class_type, start_time, end_time, days, level } = values;
    const [startH, startM] = start_time.split(':').map(Number);

    // topOffset: minutes from noon (12:00) used to vertically position
    // the class block inside the grid; the UI multiplies minutes by
    // pixels-per-minute (slotHeight/60) to get px offset.
    // const topOffset = (startH - 12) * 60 + startM;
    const topOffset = (startH - earliestHour) * 60 + startM;


    days.forEach(day => {
      if (!result[day]) result[day] = [];
      result[day].push({
        className,
        start: start_time,
        end: end_time,
        topOffset,
        type: class_type,
        level,
        overlapIndex: 0,
        overlapTotal: 1
      });
    });
  });

  // Simple overlap detection: for each day compute groups of entries that
  // overlap in time so the UI can render them side-by-side. Compute
  // overlapTotal (group size) and overlapIndex (position within the group).
  Object.keys(result).forEach(day => {
    const entries = result[day].sort((a, b) => a.topOffset - b.topOffset);
    const grouped = [];

    for (let i = 0; i < entries.length; i++) {
      const current = entries[i];
      const currentStart = parseTime(current.start);
      const currentEnd = parseTime(current.end);

      const overlapping = entries.filter((other, j) => {
        if (i === j) return false;
        const otherStart = parseTime(other.start);
        const otherEnd = parseTime(other.end);
        // Overlap if intervals intersect
        return otherStart < currentEnd && otherEnd > currentStart;
      });

      const overlapGroup = [current, ...overlapping];
      overlapGroup.forEach((item, index) => {
        item.overlapTotal = overlapGroup.length;
        item.overlapIndex = index;
      });

      // Only push the first representative of each overlap group so we
      // avoid duplicating entries when multiple overlaps are discovered.
      grouped.push(...overlapGroup.filter((_, index) => index === 0));
    }

    result[day] = grouped;
  });

  return result;
}

function parseTime(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

// Compact time range formatting. Examples:
// - '18:00' to '19:00' -> '6 - 7 PM'
// - '11:00' to '13:00' -> '11 AM - 1 PM' (different meridians)
// - preserves minutes: '9:30' to '11:00' -> '9:30 - 11 AM'
/**
 * formatTimeRangeCompact(start, end)
 * Produce a compact representation of a start/end time
 * range. The function prefers short forms when the meridians match and/or
 * there are no minute components.
 *
 * Examples:
 * - '18:00' to '19:00' -> '6 - 7 PM'
 * - '9:30' to '11:00' -> '9:30 - 11 AM'
 * - '11:00' to '13:00' -> '11 AM - 1 PM' (different meridians preserved)
 */
export function formatTimeRangeCompact(start, end) {
  // helper to split a 'HH:MM' string into parts used for formatting
  const split = (t) => {
    const [h, m] = t.split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = (h % 12) || 12;
    const minutePart = m === 0 ? '' : `:${m.toString().padStart(2, '0')}`;
    return { hour12, minutePart, suffix };
  };

  const s = split(start);
  const e = split(end);

  // If both suffixes are same (both AM or both PM) and both have no minutes,
  // display as '6 - 7 PM' (short, compact form).
  if (s.suffix === e.suffix) {
    // If both minuteParts are empty, show hour only for start and end
    if (s.minutePart === '' && e.minutePart === '') {
      return `${s.hour12} - ${e.hour12} ${e.suffix}`;
    }
    // If start has minutes but end does not: '9:30 - 11 AM'
    if (s.minutePart !== '' && e.minutePart === '') {
      return `${s.hour12}${s.minutePart} - ${e.hour12} ${e.suffix}`;
    }
    // If end has minutes (or both have minutes), show full pieces but drop repeated suffix on start
    return `${s.hour12}${s.minutePart} - ${e.hour12}${e.minutePart} ${e.suffix}`;
  }

  // Different suffixes (AM/PM) -> show full formatted pieces for clarity
  return `${formatTime12(start)} - ${formatTime12(end)}`;
}
